import { z } from 'zod';
import { WorkSiteKindSchema, transportNeedsNumber, requiredReason, isReasonSufficient, REASON_MIN_LENGTH, EsrCodeSchema } from './rail';
import {
  CoreBriefingTypeSchema,
  CreateWorkOrderSafetyApprovalSchema,
  CreateWorkOrderSafetyMeasureSchema,
} from './core';

// Compatibility barrel: existing root imports remain valid while new code can
// use explicit CORE/RAIL entrypoints.
export * from './core';
export * from './rail';

// ─── Auth ────────────────────────────────────────────────────────────────────

export const Role = z.enum(['WORKER', 'SENIOR', 'ADMIN']);
export type Role = z.infer<typeof Role>;

export const LoginSchema = z.object({
  employeeNumber: z.string().min(1),
  password: z.string().min(1),
  role: Role,
});
export type LoginDto = z.infer<typeof LoginSchema>;

export const TokenPairSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  /**
   * Момент, после которого сессия недействительна, — срок длинного токена
   * (`RefreshToken.expiresAt`), ISO-строкой.
   *
   * ЗАЧЕМ ОН КЛИЕНТУ. Длинный токен непрозрачен: это случайная строка, а не
   * JWT, и прочитать из неё срок нельзя. Пока сеть есть, это неважно — сервер
   * сам откажет просроченной сессии. Без сети становится важно: телефон
   * обязан сам ответить, пускать ли работника в приложение, и без срока он
   * может только гадать.
   *
   * Гадать нельзя, и это решение владельца (24.09.2026): если срок на сервере
   * короче принятого телефоном, работник узнает об отказе уже на объекте, при
   * появлении связи, — а должен узнать заранее, пока может что-то сделать.
   *
   * НЕОБЯЗАТЕЛЬНОЕ. Установленные сборки поля не читают и работают как
   * прежде; телефон, получивший ответ без него, считает срок неизвестным и
   * опирается на предельный срок сессии, который сервер вообще может выдать.
   */
  sessionExpiresAt: z.string().datetime().optional(),
});
export type TokenPair = z.infer<typeof TokenPairSchema>;

export const AuthResponseSchema = TokenPairSchema.extend({
  user: z.object({
    id: z.string().uuid(),
    employeeNumber: z.string(),
    name: z.string(),
    role: Role,
    // Аддитивно (для формы создания наряда): подразделение юзера и право выдавать наряд.
    subdivisionId: z.string().nullable().optional(),
    canIssueWorkOrder: z.boolean().optional(),
    // Форс-смена пароля при первом входе (создан админом / после сброса пароля).
    mustChangePassword: z.boolean().optional(),
  }),
});
export type AuthResponse = z.infer<typeof AuthResponseSchema>;

// Лёгкий список людей подразделения (для пикеров формы наряда:
// supervisor/producer/бригада) объявлен ниже, в разделе допусков: с issue #446
// он несёт ещё и основания допуска работника, а их статусы объявлены там.

export const RefreshSchema = z.object({
  refreshToken: z.string(),
});
export type RefreshDto = z.infer<typeof RefreshSchema>;

// Смена пароля залогиненным пользователем (идентификация из JWT, userId в теле нет).
export const ChangePasswordSchema = z.object({
  currentPassword: z.string().min(1),
  newPassword: z.string().min(6, 'Пароль должен быть не менее 6 символов'),
});
export type ChangePasswordDto = z.infer<typeof ChangePasswordSchema>;

export const ChangePasswordResponseSchema = TokenPairSchema.extend({ success: z.literal(true) });
export type ChangePasswordResponse = z.infer<typeof ChangePasswordResponseSchema>;

export const LogoutAllResponseSchema = z.object({ success: z.literal(true) });
export type LogoutAllResponse = z.infer<typeof LogoutAllResponseSchema>;

// ─── Station ─────────────────────────────────────────────────────────────────

// Код станции по ЕСР — шесть цифр, как на станционном штампе (ADR-008 §5).
// Схема живёт в RAIL-подпакете и здесь реэкспортируется через `export * from
// './rail'` выше.

export const StationSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  qrCode: z.string(), // произвольная строка (напр. "ST-CENTRAL-001"), не обязана быть UUID
  // Код станции по ЕСР. Пусто — код в справочнике ещё не заведён; в листе на
  // его месте прочерк, а не выдуманные цифры.
  esrCode: z.string().nullable().optional(),
  createdAt: z.string().datetime().optional(),
});
export type Station = z.infer<typeof StationSchema>;

export const CreateStationSchema = z.object({
  name: z.string().min(1),
  esrCode: EsrCodeSchema.optional(),
});
export type CreateStationDto = z.infer<typeof CreateStationSchema>;

export const UpdateStationSchema = z.object({
  name: z.string().min(1).optional(),
  // null — снять код. Отличается от отсутствия поля: не передали — не
  // трогаем. Без этой разницы ошибочно введённый код нельзя было бы убрать.
  esrCode: EsrCodeSchema.nullable().optional(),
});
export type UpdateStationDto = z.infer<typeof UpdateStationSchema>;

// Перегон — отраслевое (RAIL) расширение места работ: пара станций «от» и «до».
// В CORE понятия перегона нет: наряд видит только WorkSite (см. секцию WorkSite).
// Ссылка идёт RAIL → CORE, обратной зависимости быть не должно.
export const RailSectionSchema = z.object({
  id: z.string().uuid(),
  workSiteId: z.string().uuid(),
  fromStationId: z.string().uuid(),
  toStationId: z.string().uuid(),
  createdAt: z.string().datetime().optional(),
});
export type RailSection = z.infer<typeof RailSectionSchema>;

// ─── Авторизация Aitu Passport (ADR-004 §12) ─────────────────────────────────

/** Что подписываем. Пусто — авторизация ради самой авторизации. */
export const AituStartQuerySchema = z.object({
  documentId: z.string().uuid().optional(),
});
export type AituStartQuery = z.infer<typeof AituStartQuerySchema>;

/** Возврат провайдера: код авторизации и наша метка. */
export const AituCallbackQuerySchema = z.object({
  code: z.string().min(1),
  state: z.string().min(1),
});
export type AituCallbackQuery = z.infer<typeof AituCallbackQuerySchema>;

export const AituStartResponseSchema = z.object({
  authorizationUrl: z.string().url(),
  expiresInSeconds: z.number().int().positive(),
});
export type AituStartResponse = z.infer<typeof AituStartResponseSchema>;

// ─── Route ───────────────────────────────────────────────────────────────────

export const RoutePurposeEnum = z.enum([
  'GTP_PLANNED',
  'UNPLANNED',
  'MALFUNCTION',
  'INSPECTION',
]);
export type RoutePurpose = z.infer<typeof RoutePurposeEnum>;

// FOOT — «пешком по перегону»: короткий выезд на соседний участок, где
// транспорт не нужен. Значение в базе существует с самого начала, но в
// интерфейс не выводилось, и такой выезд оформляли автомобилем.
// RAILCAR — автомотриса (АДМ, дрезина): самоходная единица на рельсах. Номер у
// неё есть так же, как у поезда, поэтому она требует номера при оформлении.
export const TransportEnum = z.enum(['TRAIN', 'CAR', 'FOOT', 'RAILCAR']);
export type Transport = z.infer<typeof TransportEnum>;

// Предикат живёт в RAIL-подпакете; здесь — реэкспорт для совместимости.
export { transportNeedsNumber } from './rail';
// Одно правило счёта минут на сервер и клиент — см. комментарий в rail.ts.
export { minuteOfTimestamp, minutesBetweenTimestamps } from './rail';

export const RouteStatusEnum = z.enum([
  'DRAFT',
  'PENDING',
  'PENDING_WORKER',
  // Руководитель вернул заявку исполнителю на исправление. Заявка жива:
  // причина названа, исполнитель правит и подаёт снова. Раньше это состояние
  // выражалось парой «PENDING + заполненная причина», и очередь руководителя
  // было не отличить от «ждём работника».
  'NEEDS_REVISION',
  'APPROVED',
  'IN_TRANSIT',
  'ARRIVED',
  'DEPARTED',
  'RETURNED',
  'DONE_WORKER',
  'CONFIRMED',
  // Исполнитель отказался от назначения. Отдельно от REJECTED: отказ работника
  // и непринятый руководителем результат — разные события.
  'DECLINED_WORKER',
  'REJECTED',
]);
export type RouteStatus = z.infer<typeof RouteStatusEnum>;

/**
 * Журнал маршрута: возвраты на доработку, правки результата, отмены и отказы.
 *
 * Причина возврата хранится и полем на карточке, но поле держит только
 * последнюю формулировку. По нему не видно, сколько раз заявку возвращали и
 * что просили в прошлый раз, — а именно это и разбирают, когда спорят.
 */
export const RouteJournalKindEnum = z.enum([
  'ROUTE_RETURNED_FOR_REVISION',
  'ROUTE_RESULT_EDITED',
  'ROUTE_CANCELLED',
  'ROUTE_REJECTED',
]);
export type RouteJournalKind = z.infer<typeof RouteJournalKindEnum>;

export const RouteJournalEntrySchema = z.object({
  id: z.string(),
  eventKind: RouteJournalKindEnum,
  occurredAt: z.string().datetime(),
  actorUserId: z.string().nullable().optional(),
  actorEmployeeNumber: z.string().nullable().optional(),
  actorRole: z.string().nullable().optional(),
  actorUser: z.object({ id: z.string(), name: z.string() }).nullable().optional(),
  details: z.object({
    // Какая по счёту итерация — считается по самому журналу, не полем.
    iteration: z.number().optional(),
    edit: z.number().optional(),
    reason: z.string().optional(),
    // Станция цепочки, о которой шла правка итога: у поездки через две
    // станции «результат переписали» без названия ничего не говорит.
    station: z.string().optional(),
    previousStatus: z.string().nullable().optional(),
    previousComment: z.string().nullable().optional(),
    nextStatus: z.string().nullable().optional(),
    nextComment: z.string().nullable().optional(),
  }).passthrough(),
});
export type RouteJournalEntry = z.infer<typeof RouteJournalEntrySchema>;

/**
 * Станция работ в цепочке поездки (ADR-008 §2).
 *
 * Поездка — цепочка: выехал с приписки, одна или несколько станций работ,
 * вернулся. Прибыть на следующую можно только после убытия с предыдущей.
 * Поездка на одну станцию — цепочка из одной строки, а не другая сущность.
 *
 * Наименование работ и итог стоят здесь, а не у поездки: на бланке ФРУ № 12
 * они в строке прибытия на станцию, и у поездки через две станции их два.
 */
export const RouteStationSchema = z.object({
  id: z.string().uuid(),
  routeId: z.string().uuid().optional(),
  stationId: z.string().uuid(),
  station: StationSchema.optional(),
  /** Порядок в цепочке, с 1. Приписки в перечне нет — она в профиле работника. */
  sequence: z.number().int().positive(),
  /** «Наименование» работ из бланка — задаёт мастер при назначении. */
  workName: z.string().nullable().optional(),
  resultStatus: z.enum(['DONE', 'PARTIAL', 'NOT_DONE']).nullable().optional(),
  resultComment: z.string().nullable().optional(),
});
export type RouteStation = z.infer<typeof RouteStationSchema>;

/** Станция в назначении мастера: куда и что там делать. */
export const RouteStationAssignmentSchema = z.object({
  stationId: z.string().uuid(),
  workName: z.string().trim().min(1).optional(),
});
export type RouteStationAssignment = z.infer<typeof RouteStationAssignmentSchema>;

export const RouteSchema = z.object({
  id: z.string().uuid(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  workerId: z.string().uuid(),
  // ПЕРВАЯ станция цепочки. Полный перечень — в `stations`. Поля оставлены на
  // месте: по ним читают списки и уже выпущенные сборки приложения.
  stationId: z.string().uuid(),
  station: StationSchema.optional(),
  /**
   * Цепочка станций работ по порядку. У поездки, назначенной до цепочки, —
   * одна строка: перенос сделан миграцией, а не догадкой при чтении.
   */
  stations: z.array(RouteStationSchema).optional(),
  purpose: RoutePurposeEnum,
  transport: TransportEnum,
  trainNumber: z.string().nullable().optional(),
  vehicleInfo: z.string().nullable().optional(),
  status: RouteStatusEnum,
  // Два условия старта. По ним экран решает, чьё действие сейчас ожидается:
  // статус PENDING сам по себе этого больше не говорит — ждать можно и
  // исполнителя, и его руководителя, и обоих сразу.
  workerAcceptedAt: z.string().datetime().nullable().optional(),
  managerApprovedAt: z.string().datetime().nullable().optional(),
  // Смотрящий — руководитель исполнителя этого маршрута. Признак считает
  // сервер: связь «руководитель — подчинённый» клиенту не видна.
  viewerIsManager: z.boolean().optional(),
  // Опоздание к сроку прибытия, в минутах. null — приехал вовремя или раньше.
  // Считает сервер, одной формулой со статистикой: два расчёта одного числа
  // однажды разойдутся, и разбираться придётся, какое из них правда.
  arrivalDelayMinutes: z.number().nullable().optional(),
  // Журнал приходит только в карточке одного маршрута — в списке он не нужен.
  journal: z.array(RouteJournalEntrySchema).optional(),
  /**
   * Поездка открыта на исправление возвратом маршрутного листа (ADR-008 §7,
   * issue #509): работник правит № поезда и итог, добавляет пропущенную ручную
   * отметку. Приходит только в карточке одного маршрута; NULL — не открыта.
   */
  sheetCorrection: z.object({ open: z.boolean(), reason: z.string().nullable() }).nullable().optional(),
  // День фактического выезда — первая отметка маршрута. Им маршрут подписан в
  // списках, им же встаёт столбец графика: план и дата завершения расходились
  // и между собой, и с обоими.
  departedAt: z.string().datetime().nullable().optional(),
  plannedDepartureAt: z.string().datetime(),
  comment: z.string().nullable().optional(),
  resultStatus: z.enum(['DONE', 'PARTIAL', 'NOT_DONE']).nullable().optional(),
  resultComment: z.string().nullable().optional(),
  // Результат ЗАДАНИЯ, ради которого был выезд, — отдельно от состояния
  // поездки. Поездка закрывается по возвращению и попадает в учёт времени
  // независимо от того, удалось ли сделать работу.
  taskCompleted: z.boolean().optional(),
  taskFailureReason: z.string().nullable().optional(),
  // Маршрут, взамен которого создан этот. Только история переделок: на экранах
  // пока не показывается, текстовая ссылка в комментарии живёт отдельно.
  replacesRouteId: z.string().uuid().nullable().optional(),
  returnTransport: TransportEnum.nullable().optional(),
  returnTrainNumber: z.string().nullable().optional(),
  returnVehicleInfo: z.string().nullable().optional(),
  returnTime: z.string().datetime().nullable().optional(),
  completedAt: z.string().datetime().nullable().optional(),
  approvedBeforeBy: z.object({ id: z.string(), name: z.string() }).nullable().optional(),
  approvedBeforeAt: z.string().datetime().nullable().optional(),
  approvedBeforeComment: z.string().nullable().optional(),
  confirmedAfterBy: z.object({ id: z.string(), name: z.string() }).nullable().optional(),
  confirmedAfterAt: z.string().datetime().nullable().optional(),
  confirmedAfterComment: z.string().nullable().optional(),
  rejectedBy: z.object({ id: z.string(), name: z.string() }).nullable().optional(),
  rejectedAt: z.string().datetime().nullable().optional(),
  rejectionReason: z.string().nullable().optional(),
  createdById: z.string().nullable().optional(),
  createdBy: z.object({ id: z.string(), name: z.string() }).nullable().optional(),
  worker: z.object({ id: z.string(), name: z.string(), employeeNumber: z.string() }).nullable().optional(),
  safetyBriefingPassed:   z.boolean().optional(),
  safetyBriefingPassedAt: z.string().datetime().nullable().optional(),
  safetyBriefingItems: z.array(z.object({
    id: z.number(),
    text: z.string().optional(), // отображаемый текст не персистим — берётся из mobile-i18n (briefing.items.<id>)
    checked: z.boolean(),
  })).nullable().optional(),
  events: z.array(z.object({
    id:           z.string().uuid(),
    type:         z.enum(['DEPART_HOME', 'ARRIVAL', 'DEPARTURE', 'ARRIVE_HOME']),
    occurredAt:   z.string().datetime(),
    method:       z.enum(['QR', 'MANUAL_FALLBACK']),
    comment:      z.string().nullable().optional(),
    approvedAt:   z.string().datetime().nullable().optional(),
    approvedById: z.string().uuid().nullable().optional(),
    stationId:    z.string().uuid().nullable().optional(),
    stationName:  z.string().nullable().optional(),
    station:      z.object({ id: z.string(), name: z.string(), esrCode: z.string().nullable().optional() }).nullable().optional(),
    // К какой станции цепочки относится отметка. Пусто у выезда из приписки и
    // возврата: эти две относятся к поездке целиком.
    routeStationId: z.string().uuid().nullable().optional(),
    // № поезда этой отметки — необязательный, вводит работник (ADR-008 §4).
    trainNumber:  z.string().nullable().optional(),
  })).optional(),
});
export type Route = z.infer<typeof RouteSchema>;

export const CreateRouteSchema = z.object({
  /**
   * Одна станция работ — прежняя форма назначения. Оставлена, потому что её
   * присылают уже выпущенные сборки приложения; равна цепочке из одной
   * станции.
   */
  stationId: z.string().uuid().optional(),
  /**
   * Цепочка станций работ по порядку (ADR-008 §2). Порядок в массиве и есть
   * порядок поездки.
   */
  stations: z.array(RouteStationAssignmentSchema).min(1).optional(),
  purpose: RoutePurposeEnum,
  transport: TransportEnum,
  trainNumber: z.string().optional(),
  vehicleInfo: z.string().optional(),
  plannedDepartureAt: z.string().datetime(),
  comment: z.string().optional(),
  workerId: z.string().uuid().optional(),
  // Заполняется действием «создать новый маршрут на ту же работу» из списка
  // невыполненных заданий. Обычное создание маршрута поле не передаёт.
  replacesRouteId: z.string().uuid().optional(),
  safetyBriefingPassed:   z.boolean().optional(),
  safetyBriefingPassedAt: z.string().datetime().optional(),
  safetyBriefingItems: z.array(z.object({
    id: z.number(),
    text: z.string().optional(), // отображаемый текст не персистим — берётся из mobile-i18n (briefing.items.<id>)
    checked: z.boolean(),
  })).optional(),
}).superRefine((data, ctx) => {
  if (data.transport === 'CAR' && (!data.vehicleInfo || data.vehicleInfo.trim().length < 3)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['vehicleInfo'],
      message: 'Марка и номер автомобиля обязательны (не менее 3 символов)',
    });
  }
  if (data.transport && transportNeedsNumber(data.transport) && !data.trainNumber) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['trainNumber'],
      message: 'Номер обязателен при транспорте TRAIN или RAILCAR',
    });
  }
  // Куда едем — обязательно, одной формой или другой. Ни одна не сказана —
  // это не поездка, а заявка без цели.
  if (!data.stationId && !data.stations?.length) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['stations'],
      message: 'Укажите хотя бы одну станцию работ',
    });
  }
  // Одна станция дважды в цепочке — почти наверняка промах пальцем, а не
  // намерение: «прибыть туда, откуда только что убыл» переездом не является.
  const chain = data.stations?.map((s) => s.stationId) ?? [];
  if (chain.length !== new Set(chain).size) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['stations'],
      message: 'Одна и та же станция указана в цепочке дважды',
    });
  }
});
export type CreateRouteDto = z.infer<typeof CreateRouteSchema>;

export const UpdateRouteSchema = z.object({
  purpose:            RoutePurposeEnum.optional(),
  transport:          TransportEnum.optional(),
  trainNumber:        z.string().optional(),
  vehicleInfo:        z.string().optional(),
  plannedDepartureAt: z.string().datetime().optional(),
  comment:            z.string().optional(),
  safetyBriefingPassed:   z.boolean().optional(),
  safetyBriefingPassedAt: z.string().datetime().optional(),
  safetyBriefingItems: z.array(z.object({
    id: z.number(),
    text: z.string().optional(), // отображаемый текст не персистим — берётся из mobile-i18n (briefing.items.<id>)
    checked: z.boolean(),
  })).optional(),
}).superRefine((data, ctx) => {
  if (data.transport === 'CAR' && data.vehicleInfo !== undefined && data.vehicleInfo.trim().length < 3) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['vehicleInfo'], message: 'Марка и номер автомобиля (не менее 3 символов)' });
  }
  if (data.transport && transportNeedsNumber(data.transport) && data.trainNumber !== undefined && data.trainNumber.trim().length === 0) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['trainNumber'], message: 'Номер не может быть пустым' });
  }
});
export type UpdateRouteDto = z.infer<typeof UpdateRouteSchema>;

export const PassBriefingSchema = z.object({
  safetyBriefingPassedAt: z.string().datetime(),
  safetyBriefingItems: z.array(z.object({
    id: z.number(),
    text: z.string().optional(), // отображаемый текст не персистим — берётся из mobile-i18n (briefing.items.<id>)
    checked: z.boolean(),
  })),
});
export type PassBriefingDto = z.infer<typeof PassBriefingSchema>;

// Стабильные id пунктов целевого инструктажа. Отображаемый текст живёт в mobile-i18n
// под ключами briefing.items.<id> (ru+kk); в Route.safetyBriefingItems пишем id, не текст.
export const SAFETY_BRIEFING_ITEM_IDS = [1, 2, 3, 4] as const;

// ─── RouteEvent ───────────────────────────────────────────────────────────────

export const EventTypeEnum = z.enum(['DEPART_HOME', 'ARRIVAL', 'DEPARTURE', 'ARRIVE_HOME']);
export type EventType = z.infer<typeof EventTypeEnum>;

export const EventMethodEnum = z.enum(['QR', 'MANUAL_FALLBACK']);
export type EventMethod = z.infer<typeof EventMethodEnum>;

export const RouteEventSchema = z.object({
  id: z.string().uuid(),
  routeId: z.string().uuid(),
  type: EventTypeEnum,
  occurredAt: z.string().datetime(),
  latitude: z.number().nullable().optional(),
  longitude: z.number().nullable().optional(),
  gpsAccuracy: z.number().nullable().optional(),
  method: EventMethodEnum,
  comment: z.string().nullable().optional(),
  approvedAt: z.string().datetime().nullable().optional(),
  approvedById: z.string().uuid().nullable().optional(),
  createdAt: z.string().datetime(),
});
export type RouteEvent = z.infer<typeof RouteEventSchema>;

/**
 * № поезда отметки — необязательный, и система его НЕ проверяет (ADR-008 §4).
 *
 * На бланке ФРУ № 12 он стоит в каждой строке выбытия и прибытия. Пусто —
 * прочерк в графе: поездка автомобилем, автомотрисой или пешком. Сверять его
 * не с чем: расписания поездов в системе нет, и «проверка» свелась бы к
 * запрету непривычной записи.
 *
 * Ограничение длины — от опечатки в буфере обмена, а не от формы номера:
 * номер поезда бывает и «6021», и «6021/6022», и с буквой.
 */
export const EventTrainNumberSchema = z.string().trim().max(32);

export const ScanArrivalSchema = z.object({
  qrCode: z.string().uuid().optional(),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
  gpsAccuracy: z.number().optional(),
  method: EventMethodEnum,
  comment: z.string().optional(),
  trainNumber: EventTrainNumberSchema.optional(),
});
export type ScanArrivalDto = z.infer<typeof ScanArrivalSchema>;

/**
 * Исправление поездки после возврата маршрутного листа (ADR-008 §7, issue
 * #509): работник правит то, что вводил сам. № поезда — отметки, у которой он
 * стоит; время и станция отметки не меняются никем.
 *
 * `null` или пустая строка — «номер не указан»: прочерк в графе листа.
 */
export const EditRouteEventTrainNumberSchema = z.object({
  trainNumber: EventTrainNumberSchema.nullable(),
});
export type EditRouteEventTrainNumberDto = z.infer<typeof EditRouteEventTrainNumberSchema>;

/**
 * Пропущенная ручная отметка на станции цепочки — после возврата листа (issue
 * #509). ЕДИНСТВЕННАЯ отметка, время которой называет человек, а не сервер:
 * добавляют её задним числом, и серверное «сейчас» стояло бы после возвращения
 * домой. Поэтому она ручная, с объяснением, и в лист идёт только после
 * подтверждения другим старшим — как любая ручная отметка (решение В4).
 */
export const AddMissedManualMarkSchema = z.object({
  type: z.enum(['ARRIVAL', 'DEPARTURE']),
  routeStationId: z.string().uuid(),
  occurredAt: z.string().datetime(),
  comment: z.string().trim().min(1, 'Объясните, почему отметки нет').max(500),
  trainNumber: EventTrainNumberSchema.optional(),
});
export type AddMissedManualMarkDto = z.infer<typeof AddMissedManualMarkSchema>;

export const ScanDepartureSchema = z.object({
  qrCode: z.string().uuid().optional(),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
  gpsAccuracy: z.number().optional(),
  method: EventMethodEnum,
  comment: z.string().optional(),
  trainNumber: EventTrainNumberSchema.optional(),
  // Данные обратной поездки — фиксируются на убытии (точка 3), по образцу поездки «туда»
  returnTransport: TransportEnum,
  returnTrainNumber: z.string().optional(),
  returnVehicleInfo: z.string().optional(),
  returnTime: z.string().datetime(),
}).superRefine((data, ctx) => {
  if (transportNeedsNumber(data.returnTransport) && !data.returnTrainNumber) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['returnTrainNumber'],
      message: 'Номер обязателен при транспорте TRAIN или RAILCAR',
    });
  }
  if (data.returnTransport === 'CAR' && (!data.returnVehicleInfo || data.returnVehicleInfo.trim().length < 3)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['returnVehicleInfo'],
      message: 'Марка и номер автомобиля обязательны (не менее 3 символов)',
    });
  }
});
export type ScanDepartureDto = z.infer<typeof ScanDepartureSchema>;

// Все DTO точек несут только GPS + method. Времени (occurredAt) НЕТ ни в одной —
// метку времени каждой точки ставит сервер. method=MANUAL_FALLBACK — это и есть
// признак ручной отметки; comment несёт необязательную причину ручного ввода.

// Точка 1 — выезд из приписки (APPROVED → IN_TRANSIT)
export const ScanDepartHomeSchema = z.object({
  qrCode: z.string().uuid().optional(),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
  gpsAccuracy: z.number().optional(),
  method: EventMethodEnum,
  comment: z.string().optional(),
  // Не передан — сервер подставит № поезда поездки: это первая строка листа,
  // и он уже назван при оформлении (ADR-008 §4).
  trainNumber: EventTrainNumberSchema.optional(),
});
export type ScanDepartHomeDto = z.infer<typeof ScanDepartHomeSchema>;

// Точка 4 — возврат в приписку (DEPARTED → RETURNED)
export const ScanReturnSchema = z.object({
  qrCode: z.string().uuid().optional(),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
  gpsAccuracy: z.number().optional(),
  method: EventMethodEnum,
  comment: z.string().optional(),
  // Не передан — сервер подставит № поезда обратной дороги: это последняя
  // строка листа.
  trainNumber: EventTrainNumberSchema.optional(),
});
export type ScanReturnDto = z.infer<typeof ScanReturnSchema>;

// Подтверждение ручной отметки старшим — без времени, approvedAt ставит сервер.
// stationId обязателен: при ручной фиксации станцию не сканировали, старший
// проставляет её из справочника при подтверждении (замечание 2).
export const ApproveEventSchema = z.object({
  stationId: z.string().uuid(),
  comment: z.string().optional(),
});
export type ApproveEventDto = z.infer<typeof ApproveEventSchema>;

// ─── RouteResult ─────────────────────────────────────────────────────────────

export const ResultStatusEnum = z.enum(['DONE', 'PARTIAL', 'NOT_DONE']);
export type ResultStatus = z.infer<typeof ResultStatusEnum>;

export const SaveResultSchema = z.object({
  /**
   * Станция цепочки, о которой идёт речь (ADR-008 §2). Не передана — сервер
   * берёт ту, на которой работник сейчас находится: прибыл и ещё не убыл.
   *
   * Оставлена необязательной, потому что уже выпущенные сборки приложения её
   * не присылают, а у поездки на одну станцию выбора всё равно нет.
   */
  routeStationId: z.string().uuid().optional(),
  resultStatus: ResultStatusEnum,
  resultComment: z.string().optional(),
  // Задание и поездка — разные сущности: речь про работу на объекте, а не про
  // состояние выезда. Незаконченная работа — это и «не выполнено», и
  // «частично»: доделывать нужно и то и другое. Закрыть поездку она не мешает
  // и из учёта времени её не убирает.
  taskCompleted: z.boolean().optional(),
  taskFailureReason: z.string().optional(),
}).superRefine((data, ctx) => {
  if (data.taskCompleted === false && !isReasonSufficient(data.taskFailureReason)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['taskFailureReason'],
      message: `Незаконченную работу нужно объяснить: не короче ${REASON_MIN_LENGTH} символов`,
    });
  }
  if (data.resultStatus !== 'DONE') {
    if (!isReasonSufficient(data.resultComment)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['resultComment'],
        message: 'Комментарий обязателен (не менее 10 символов) при статусе PARTIAL или NOT_DONE',
      });
    }
  }
});
export type SaveResultDto = z.infer<typeof SaveResultSchema>;

// ─── Approval ────────────────────────────────────────────────────────────────

export const ApprovalActionEnum = z.enum(['APPROVE', 'REJECT']);
export type ApprovalAction = z.infer<typeof ApprovalActionEnum>;

const approvalBase = z.object({
  action: ApprovalActionEnum,
  comment: z.string().optional(),
}).superRefine((data, ctx) => {
  if (data.action === 'REJECT' && !isReasonSufficient(data.comment)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['comment'],
      message: `Причина отклонения обязательна (не менее ${REASON_MIN_LENGTH} символов)`,
    });
  }
});

// Отмена принятой заявки исполнителем. Причина обязательна: заявка исчезает
// из смены, и без причины руководитель не знает, почему выезд не состоялся.
export const CancelRouteSchema = z.object({
  reason: requiredReason(`Причина отмены обязательна: не короче ${REASON_MIN_LENGTH} символов`),
});
export type CancelRouteDto = z.infer<typeof CancelRouteSchema>;

// Отказ исполнителя от назначения. Причина обязательна и проверяется тем же
// правилом, что причина отклонения руководителем: назначивший должен прочитать,
// почему выезд не состоится, и решить, кого назначить вместо.
export const DeclineRouteSchema = z.object({
  reason: requiredReason(`Причина отказа обязательна: не короче ${REASON_MIN_LENGTH} символов`),
});
export type DeclineRouteDto = z.infer<typeof DeclineRouteSchema>;

export const ApproveBeforeSchema = approvalBase;
export type ApproveBeforeDto = z.infer<typeof ApproveBeforeSchema>;

export const ApproveAfterSchema = approvalBase;
export type ApproveAfterDto = z.infer<typeof ApproveAfterSchema>;

// ─── WorkConsent ─────────────────────────────────────────────────────────────

export const WorkTypeEnum = z.enum(['WEEKEND', 'HOLIDAY', 'OVERTIME']);
export type WorkType = z.infer<typeof WorkTypeEnum>;

export const ConsentStatusEnum = z.enum(['PENDING', 'ACCEPTED', 'DECLINED']);
export type ConsentStatus = z.infer<typeof ConsentStatusEnum>;

export const CompensationTypeSchema = z.enum(['MONEY', 'DAY_OFF']);
export type CompensationType = z.infer<typeof CompensationTypeSchema>;

export const WorkConsentSchema = z.object({
  id: z.string().uuid(),
  workerId: z.string().uuid(),
  worker: z.object({ id: z.string(), name: z.string(), employeeNumber: z.string() }).optional(),
  createdById: z.string().uuid(),
  createdBy: z.object({ id: z.string(), name: z.string() }).optional(),
  workDate: z.string(),
  workType: WorkTypeEnum,
  reason: z.string().nullable().optional(),
  status: ConsentStatusEnum,
  compensationType: CompensationTypeSchema.nullable().optional(),
  respondedAt: z.string().datetime().nullable().optional(),
  // Крайний срок изменения ответа: минимум из суток от ответа и начала
  // рабочего дня по заявке. Считает сервер — тот же расчёт, что и проверка.
  changeDeadlineAt: z.string().datetime().nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type WorkConsent = z.infer<typeof WorkConsentSchema>;

export const AcceptConsentBodySchema = z.object({
  compensationType: CompensationTypeSchema,
});
export type AcceptConsentBody = z.infer<typeof AcceptConsentBodySchema>;

export const CreateConsentSchema = z.object({
  workerId: z.string().uuid(),
  workDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Формат даты: YYYY-MM-DD'),
  workType: WorkTypeEnum,
  reason: z.string().optional(),
});
export type CreateConsentDto = z.infer<typeof CreateConsentSchema>;

export const UpdateConsentSchema = z.object({
  workDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Формат даты: YYYY-MM-DD').optional(),
  workType: WorkTypeEnum.optional(),
  reason:   z.string().optional(),
});
export type UpdateConsentDto = z.infer<typeof UpdateConsentSchema>;

export const ChangeConsentResponseSchema = z.object({
  newStatus: z.enum(['ACCEPTED', 'DECLINED']),
});
export type ChangeConsentResponseDto = z.infer<typeof ChangeConsentResponseSchema>;

export const WorkerSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  employeeNumber: z.string(),
});
export type Worker = z.infer<typeof WorkerSchema>;

// ─── Управление работниками (модуль users, ADMIN) ──────────────────────────────

// Полное представление работника для админ-экрана (в отличие от frozen WorkerSchema
// {id,name,employeeNumber}, который используют пикеры наряда).
export const WorkerAdminSchema = z.object({
  id: z.string().uuid(),
  employeeNumber: z.string(),
  name: z.string(),
  role: Role,
  subdivisionId: z.string().nullable().optional(),
  homeStationId: z.string().nullable().optional(),
  canIssueWorkOrder: z.boolean(),
  isActive: z.boolean(),
  // Issue #142: профессия по ЕТКС и присвоенный разряд. Хранятся на
  // OrganizationMembership, поэтому приходят только из tenant-scoped чтения;
  // аддитивно и optional — старые клиенты не ломаются.
  professionId: z.string().nullable().optional(),
  professionName: z.string().nullable().optional(),
  qualificationGrade: z.number().int().nullable().optional(),
  // Issue #531: должность сотрудника (графа 4 журнала инструктажа вместе с
  // профессией) и год рождения (графа 3). Год вычислен из ИИН при его вводе
  // и руками не вводится; ИИН для него не расшифровывается.
  position: z.string().nullable().optional(),
  birthYear: z.number().int().nullable().optional(),
});
export type WorkerAdmin = z.infer<typeof WorkerAdminSchema>;

export const CreateWorkerSchema = z.object({
  employeeNumber: z.string().min(1),
  name: z.string().min(1),
  role: Role,
  subdivisionId: z.string().uuid().nullable().optional(),
  homeStationId: z.string().uuid().nullable().optional(),
  password: z.string().min(6, 'Пароль должен быть не менее 6 символов'),
  canIssueWorkOrder: z.boolean().optional(), // дефолт false на сервере
  // Issue #142: профессия из справочника организации и разряд. Сервер
  // проверяет принадлежность записи организации и попадание разряда в
  // диапазон [min, max] этой записи; разряд без профессии отклоняется.
  professionId: z.string().uuid().nullable().optional(),
  qualificationGrade: z.number().int().positive().max(99).nullable().optional(),
});
export type CreateWorkerDto = z.infer<typeof CreateWorkerSchema>;

// Табельный неизменяем, пароля здесь нет (для пароля — отдельный reset-password).
export const UpdateWorkerSchema = z.object({
  name: z.string().min(1).optional(),
  role: Role.optional(),
  subdivisionId: z.string().uuid().nullable().optional(),
  homeStationId: z.string().uuid().nullable().optional(),
  canIssueWorkOrder: z.boolean().optional(),
  // Issue #142: см. CreateWorkerSchema. null в professionId снимает и
  // профессию, и разряд (разряд без профессии невозможен).
  professionId: z.string().uuid().nullable().optional(),
  qualificationGrade: z.number().int().positive().max(99).nullable().optional(),
});
export type UpdateWorkerDto = z.infer<typeof UpdateWorkerSchema>;

export const ResetPasswordSchema = z.object({
  newPassword: z.string().min(6, 'Пароль должен быть не менее 6 символов'),
});
export type ResetPasswordDto = z.infer<typeof ResetPasswordSchema>;

export const ResetPasswordResponseSchema = z.object({ success: z.boolean() });
export type ResetPasswordResponse = z.infer<typeof ResetPasswordResponseSchema>;

// Опция подразделения для пикера в форме работника (нет отдельного модуля subdivisions —
// лёгкий read в модуле users).
export const SubdivisionOptionSchema = z.object({ id: z.string().uuid(), name: z.string() });
export type SubdivisionOption = z.infer<typeof SubdivisionOptionSchema>;

// ─── Stats ────────────────────────────────────────────────────────────────────

export const StatsTimeQuerySchema = z.object({
  from:  z.string().datetime(),
  to:    z.string().datetime(),
  scope: z.enum(['me', 'all']).default('me'),
});
export type StatsTimeQuery = z.infer<typeof StatsTimeQuerySchema>;

export const StatsTimeResponseSchema = z.object({
  totalMinutes: z.number(),
  routesCount:  z.number(),
  byDay: z.array(z.object({
    date:    z.string(),
    minutes: z.number(),
  })),
  routes: z.array(z.object({
    id:                   z.string(),
    plannedDepartureAt:   z.string().datetime(),
    completedAt:          z.string().datetime(),
    stationName:          z.string(),
    purpose:              RoutePurposeEnum,
    durationMinutes:      z.number(),
    workerName:           z.string(),
    workerEmployeeNumber: z.string(),
    // Сколько точек маршрута зафиксировано (убытие из приписки, прибытие,
    // убытие обратно, прибытие домой) и сколько из них по QR. По ним видно,
    // что время посчитано по факту, а не по плану.
    pointsRecorded:       z.number(),
    pointsByQr:           z.number(),
    // Время посчитано по зафиксированным точкам (убытие → прибытие домой),
    // а не по плановому отправлению и отметке завершения.
    durationFromPoints:   z.boolean(),
    // Опоздание к сроку прибытия, в минутах. null — приехал вовремя или
    // раньше: приехать заранее нормально и отклонением не считается.
    arrivalDelayMinutes:  z.number().nullable().optional(),
    // День фактического выезда — им поездка подписана в списке и им же встаёт
    // столбец графика. Дата плана и дата завершения расходились с обоими.
    departedAt:           z.string().datetime().optional(),
    // Разложение того же времени: дорога туда и обратно против работы на
    // объекте. В «часах в пути» они складывались в одно число, и по нему
    // нельзя было сказать, сколько человек ехал, а сколько работал.
    travelMinutes:        z.number().nullable().optional(),
    onSiteMinutes:        z.number().nullable().optional(),
    // Issue #521: может ли смотрящий открыть карточку этого маршрута — по
    // тому же правилу, что сама карточка (`route-visibility.ts`). Статистика
    // «по всем» у старшего показывает поездки всей организации, а открыть он
    // может только «свои»; без признака нажатие на чужую строку давало «Маршрут
    // не найден». Необязательное — ради уже установленных сборок.
    viewerCanOpen:        z.boolean().optional(),
  })),
  // Issue #521: завершённые маршруты, которые в итоги НЕ вошли, и почему.
  // Раньше они выпадали молча: поездка есть, в часах её нет, и не видно, что
  // её не посчитали. В `totalMinutes`, `routesCount` и `byDay` их нет.
  skipped: z.array(z.object({
    id:                   z.string(),
    completedAt:          z.string().datetime(),
    stationName:          z.string(),
    workerName:           z.string(),
    workerEmployeeNumber: z.string(),
    // NO_DEPARTURE_OR_RETURN — нет отметки выезда или возврата, время в пути
    // посчитать не по чему. NEGATIVE_DURATION — возврат отмечен раньше выезда.
    reason:               z.enum(['NO_DEPARTURE_OR_RETURN', 'NEGATIVE_DURATION']),
    viewerCanOpen:        z.boolean(),
  })).optional(),
  // Маршруты, которые идут прямо сейчас. Показываются отдельным счётчиком:
  // в часы они не идут (поездка не завершена), но ноль вместо них человек
  // читает как «система меня не видит».
  inProgressCount: z.number().optional(),
});
export type StatsTimeResponse = z.infer<typeof StatsTimeResponseSchema>;

// ─── RailSafe: Clearances (контроль допусков) ──────────────────────────────────

export const ClearanceCategoryEnum = z.enum(['BRIEFING', 'ELECTRICAL_SAFETY', 'MEDICAL']);
export type ClearanceCategory = z.infer<typeof ClearanceCategoryEnum>;

// Природа типа допуска: периодический (со сроком) / разовый (без срока) / событие (вне ворот).
export const ClearanceKindEnum = z.enum(['PERIODIC', 'ONE_TIME', 'EVENT']);
export type ClearanceKind = z.infer<typeof ClearanceKindEnum>;

export const ClearanceStatusEnum = z.enum(['active', 'expiring_soon', 'expired']);
export type ClearanceStatus = z.infer<typeof ClearanceStatusEnum>;

// Группа по электробезопасности (нормативная). Ординал: II=2..V=5.
export const ElectricalGroupEnum = z.enum(['II', 'III', 'IV', 'V']);
export type ElectricalGroup = z.infer<typeof ElectricalGroupEnum>;
export const ELECTRICAL_GROUP_ORDINAL: Record<ElectricalGroup, number> = { II: 2, III: 3, IV: 4, V: 5 };

// Issue #419 (карточка B-057): напряжение электроустановки. Пункты 33, 35, 37
// и 38 Правил № 222 задают пороги группы по электробезопасности двумя
// наборами — для работ выше 1000 В и для работ до 1000 В, — и без этого
// признака требуемую группу нельзя вывести из нормы.
export const InstallationVoltageClassEnum = z.enum(['ABOVE_1000V', 'UP_TO_1000V']);
export type InstallationVoltageClass = z.infer<typeof InstallationVoltageClassEnum>;

export const OverallStatusEnum = z.enum(['admitted', 'warning', 'not_admitted']);
export type OverallStatus = z.infer<typeof OverallStatusEnum>;

// ─── Основания допуска работника в списках людей ─────────────────────────────
//
// Один и тот же набор отвечает на вопрос «кого можно ставить в бригаду» в
// списке персонала (#444) и в выборе людей при выписке наряда (#446).
// Считает его СЕРВЕР — тем же расчётом, что светофор допуска работника
// (`clearances.logic.ts`). Второй расчёт в клиенте был бы вторым ответом на
// вопрос «проходит ли работник по медосмотру», и разошёлся бы с первым молча.

/**
 * Четвёртое состояние сверх трёх серверных: записи нет вовсе.
 *
 * «Просрочен» и «нет записи» — разные вещи для того, кто решает, кого ставить
 * в бригаду: в первом случае документ был и его надо продлить, во втором его
 * не было никогда.
 */
export const PersonnelClearanceStatusEnum = z.enum([...ClearanceStatusEnum.options, 'missing']);
export type PersonnelClearanceStatus = z.infer<typeof PersonnelClearanceStatusEnum>;

export const PersonnelClearanceSchema = z.object({
  /** `OrganizationClearanceType.id` — ключ колонки, одинаковый во всех строках. */
  typeId: z.string().uuid(),
  /** Название типа допуска так, как его назвала организация. */
  name: z.string(),
  status: PersonnelClearanceStatusEnum,
  /** `YYYY-MM-DD`; `null` — бессрочный документ или записи нет. */
  expiresAt: z.string().nullable(),
  /** Группа по электробезопасности из самой записи допуска (II..V). */
  electricalSafetyGroup: ElectricalGroupEnum.nullable(),
  /**
   * Что это за допуск ПО СУТИ — категория и вид шаблона, из которого
   * организация завела свой тип.
   *
   * ЗАЧЕМ. Название вида допуска организация пишет как хочет: «Медосмотр»,
   * «Медицинский осмотр», «Предсменный медосмотр». Экран, которому нужно
   * найти именно медосмотр или именно повторный инструктаж, по названию этого
   * надёжно не сделает — а по категории и виду делает однозначно: медосмотр —
   * MEDICAL, группа — ELECTRICAL_SAFETY, повторный инструктаж — BRIEFING и
   * PERIODIC (вводный и первичный разовые, внеплановый — событие).
   *
   * Необязательные: прежние сборки поля не читают, а ответ без них прежним
   * клиентам не мешает.
   */
  category: ClearanceCategoryEnum.optional(),
  kind: ClearanceKindEnum.optional(),
  /**
   * Issue #520: запись есть, но она ничего не удостоверяет — у допуска по
   * электробезопасности не указан номер группы.
   *
   * Это не «просрочен» и не «нет записи», а третья беда, и называть её надо
   * своими словами: продлевать нечего и заводить заново нечего — надо
   * дописать группу в уже существующую запись.
   *
   * Решает СЕРВЕР (`clearances.logic.ts`, `electricalGroupMissing`) — тем же
   * расчётом, которым считает итог по работнику. Признак приезжает только
   * когда он верен: `undefined` значит «с этой записью такой беды нет».
   */
  electricalGroupMissing: z.literal(true).optional(),
});
export type PersonnelClearance = z.infer<typeof PersonnelClearanceSchema>;

export const PersonnelAdmissionSchema = z.object({
  clearances: z.array(PersonnelClearanceSchema),
  overallStatus: OverallStatusEnum,
  allowed: z.boolean(),
});
export type PersonnelAdmission = z.infer<typeof PersonnelAdmissionSchema>;

// Лёгкий список людей подразделения (для пикеров формы наряда:
// supervisor/producer/бригада).
/** Можно ли назначить на роль при работе до и выше 1000 В. */
export const VoltageEligibilitySchema = z.object({
  upTo1000V: z.boolean(),
  above1000V: z.boolean(),
});
export type VoltageEligibility = z.infer<typeof VoltageEligibilitySchema>;

export const SubdivisionMemberSchema = z.object({
  id: z.string().uuid(),
  employeeNumber: z.string(),
  name: z.string(),
  role: Role,
  canIssueWorkOrder: z.boolean(),
  // Issue #143: профессия по ЕТКС и разряд рядом с ФИО, чтобы бригадир
  // видел, кого он берёт. Аддитивно и optional — старые клиенты не ломаются.
  // `professionProvisional` приезжает вместе с наименованием: собирающий
  // бригаду не имеет права читать справочник профессий целиком, поэтому
  // пометку о несверенности нельзя достать отдельным запросом.
  professionName: z.string().nullable().optional(),
  qualificationGrade: z.number().int().nullable().optional(),
  professionProvisional: z.boolean().nullable().optional(),
  // Issue #446: основания допуска рядом с фамилией. Пункт 32 Правил № 222
  // возлагает соответствие групп перечисленных в наряде работников на
  // выдающего наряд, и по этому же основанию сервер отказывает в выписке
  // (#420, карточка B-058). Выбирая вслепую, выдающий узнавал об этом только
  // после заполнения всей формы.
  //
  // Аддитивно и optional: установленные сборки поля не читают и работают как
  // прежде.
  clearances: z.array(PersonnelClearanceSchema).optional(),
  overallStatus: OverallStatusEnum.optional(),
  allowed: z.boolean().optional(),
  // На какие роли в наряде работника можно назначить — отдельно для работ до
  // и выше 1000 В (порог группы зависит от напряжения). Считает сервер тем же
  // правилом, что проверяет выписку: полномочие роли и группа по пунктам
  // 33–39 Правил № 222. Форма выписки показывает в списке ответственных
  // только проходящих, а не узнаёт об отказе после отправки.
  //
  // Аддитивно и optional: установленные сборки поле не читают, а форма без
  // него (старый сервер) показывает всех, как прежде.
  assignableAs: z
    .object({
      RESPONSIBLE: VoltageEligibilitySchema,
      ADMITTING: VoltageEligibilitySchema,
      PRODUCER: VoltageEligibilitySchema,
      OBSERVER: VoltageEligibilitySchema,
    })
    .optional(),
});
export type SubdivisionMember = z.infer<typeof SubdivisionMemberSchema>;

export const ClearanceTypeSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  category: ClearanceCategoryEnum,
  kind: ClearanceKindEnum,
  renewalPeriodDays: z.number().int().nullable().optional(),
  warningThresholdDays: z.number().int(),
  isRequired: z.boolean(),
  isActive: z.boolean(),
});
export type ClearanceType = z.infer<typeof ClearanceTypeSchema>;

export const CreateClearanceTypeSchema = z.object({
  code: z.string().min(1),
  name: z.string().min(1),
  category: ClearanceCategoryEnum,
  kind: ClearanceKindEnum.optional(), // дефолт PERIODIC на стороне сервиса
  renewalPeriodDays: z.number().int().positive().optional(),
  warningThresholdDays: z.number().int().positive().optional(), // дефолт 30 на стороне сервиса
  isRequired: z.boolean().optional(),
});
export type CreateClearanceTypeDto = z.infer<typeof CreateClearanceTypeSchema>;

export const UpdateClearanceTypeSchema = z.object({
  code: z.string().min(1).optional(),
  name: z.string().min(1).optional(),
  category: ClearanceCategoryEnum.optional(),
  kind: ClearanceKindEnum.optional(),
  renewalPeriodDays: z.number().int().positive().nullable().optional(),
  warningThresholdDays: z.number().int().positive().optional(),
  isRequired: z.boolean().optional(),
  isActive: z.boolean().optional(),
});
export type UpdateClearanceTypeDto = z.infer<typeof UpdateClearanceTypeSchema>;

export const ClearanceSchema = z.object({
  id: z.string().uuid(),
  workerId: z.string().uuid(),
  clearanceTypeId: z.string().uuid(),
  documentNumber: z.string().nullable().optional(),
  electricalSafetyGroup: ElectricalGroupEnum.nullable().optional(),
  // Issue #537: начало действия допуска. У записей, внесённых после #537,
  // совпадает с датой документа; у старых записей дата документа может быть
  // пустой, а начало действия известно всегда.
  issuedAt: z.string(),
  // Issue #537: дата документа-основания, `YYYY-MM-DD`; `null` — не заполнена
  // (бывает только у записей, внесённых до обязательных номера и даты).
  documentDate: z.string().nullable().optional(),
  // Issue #537: у записи нет документа-основания — пуст номер (или из одних
  // пробелов) либо дата документа. Признак самой записи: действует ли она,
  // решает вызывающий по статусу и аннулированию.
  basisMissing: z.boolean().optional(),
  expiresAt: z.string().nullable().optional(),
  issuedBy: z.string(),
  // Snapshot ФИО и таб.номера фактически выдавшего документ. Стабильны при
  // изменении User и всегда присутствуют: миграция делает столбцы NOT NULL
  // и backfill'ит легаси-строки маркерами `historical import`, так что
  // response этих полей никогда не null.
  issuedByNameSnapshot: z.string(),
  issuedByEmployeeNumberSnapshot: z.string(),
  // FK на User опциональна: EXTERNAL_PERSON и легаси-строки не имеют
  // внутреннего пользователя. `ON DELETE SET NULL` тоже может обнулить.
  issuedByUserId: z.string().uuid().nullable().optional(),
  // Табельный оператора, внёсшего запись. Отдельно от выдающего физлица;
  // столбец в БД остаётся nullable для легаси-строк без известного оператора.
  createdByEmployeeNumber: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  // Issue #523: отметка аннулирования. Физического удаления записи больше нет:
  // аннулированная запись не даёт допуск нигде, но остаётся в истории — иначе
  // на вопрос «а был ли вообще такой документ» отвечать нечем.
  annulledAt: z.string().nullable().optional(),
  annulledByEmployeeNumber: z.string().nullable().optional(),
  annulmentReason: z.string().nullable().optional(),
  // Issue #543: откуда запись. `BRIEFING_JOURNAL` — создана записью журнала
  // инструктажа (первичный, повторный, внеплановый), номер документа — ссылка
  // на эту запись. `MANUAL` — внесена вручную: для этих видов инструктажа —
  // только старые данные, проведённые до перехода на приложение.
  source: z.enum(['MANUAL', 'BRIEFING_JOURNAL']).optional(),
});
export type Clearance = z.infer<typeof ClearanceSchema>;

export const ClearanceWithStatusSchema = ClearanceSchema.extend({
  computedStatus: ClearanceStatusEnum,
  clearanceType: ClearanceTypeSchema.optional(),
});
export type ClearanceWithStatus = z.infer<typeof ClearanceWithStatusSchema>;

// Issue #523: жёсткого удаления записи допуска больше нет — `DELETE
// /clearances/:id` снят, вместо него `POST /clearances/:id/annul`. Ответ на
// аннулирование — сама запись с отметкой, поэтому отдельной формы ответа у
// него нет; `DeleteClearanceResponse` удалён вместе с ручкой.

export const BlockedReasonSchema = z.object({
  clearanceType: z.string(),
  // `electrical_group_missing` (issue #520): запись допуска по
  // электробезопасности есть, но номера группы в ней нет. Отдельная причина, а
  // не `missing`: документ существует, и правится он дописыванием группы, а не
  // выдачей нового.
  //
  // Перечень закрытый, и новое значение в нём — не безразличная правка:
  // установленная сборка со старым перечнем такой ответ не разберёт вовсе.
  // Принято осознанно — сказать «истёк» или «нет записи» про действующий
  // документ значит отправить человека делать не то, — и безопасно сейчас:
  // приложение выпускается из этого же репозитория и обновляется вместе с
  // сервером, пилотного выпуска ещё не было.
  reason: z.enum(['missing', 'expired', 'no_requirements_configured', 'electrical_group_missing']),
  expiresAt: z.string().nullable().optional(),
});
export type BlockedReason = z.infer<typeof BlockedReasonSchema>;

export const WarningReasonSchema = z.object({
  clearanceType: z.string(),
  expiresAt: z.string(),
  daysLeft: z.number().int(),
});
export type WarningReason = z.infer<typeof WarningReasonSchema>;

export const WorkerClearancesResponseSchema = z.object({
  // Issue #523: только действующие записи — аннулированные сюда не попадают
  // вовсе. Показать их здесь значило бы дать вызывающему шанс зачесть
  // аннулированный документ; вся история, включая аннулированные, живёт в
  // `GET /clearances/worker/:employeeNumber/history`.
  clearances: z.array(ClearanceWithStatusSchema),
  overallStatus: OverallStatusEnum,
  allowed: z.boolean(),
  blockedReasons: z.array(BlockedReasonSchema).optional(),
  warningReasons: z.array(WarningReasonSchema).optional(),
  /// Issue #523: насколько старым может быть документ-основание в этой
  /// организации (`Organization.clearanceDocumentMaxAgeDays`). Приходит вместе
  /// с допусками, чтобы форма ввода показала ограничение ДО отказа, а не
  /// после него.
  documentDateMaxAgeDays: z.number().int(),
});
export type WorkerClearancesResponse = z.infer<typeof WorkerClearancesResponseSchema>;

// ─── Issue #523: журнал допусков работника ───────────────────────────────────
//
// Отвечает на вопрос «что происходило с допусками этого человека»: ввод,
// продление, исправление и аннулирование, от новых к старым. Отдельно от
// светофора: тот говорит, допущен ли человек СЕЙЧАС, а журнал — как к этому
// пришли и кто за это отвечает.

export const ClearanceHistoryChangeSchema = z.object({
  /// Поле записи допуска так, как оно называется в ответе сервера.
  field: z.string(),
  /// Значение до изменения; при вводе «было» пусто.
  from: z.string().nullable(),
  /// Значение после изменения; пусто, если поле очистили.
  to: z.string().nullable(),
});
export type ClearanceHistoryChange = z.infer<typeof ClearanceHistoryChangeSchema>;

export const ClearanceHistoryEventSchema = z.object({
  id: z.string().uuid(),
  clearanceId: z.string().uuid().nullable(),
  /// `CLEARANCE_CREATED` / `CLEARANCE_RENEWED` / `CLEARANCE_UPDATED` /
  /// `CLEARANCE_BASIS_COMPLETED` / `CLEARANCE_ANNULLED` и исторический
  /// `CLEARANCE_DELETED`. Строкой, а не
  /// перечнем: журнал показывает и то, что писали прежние версии сервера.
  eventKind: z.string(),
  /// Время ввода записи журнала — когда действие совершили.
  occurredAt: z.string(),
  /// Дата документа-основания — отдельно от времени ввода: документ выдан
  /// одним числом, внесён другим, и путать их нельзя.
  documentDate: z.string().nullable(),
  actorEmployeeNumber: z.string().nullable(),
  actorName: z.string().nullable(),
  actorRole: z.string().nullable(),
  clearanceTypeName: z.string().nullable(),
  reason: z.string().nullable(),
  changes: z.array(ClearanceHistoryChangeSchema),
});
export type ClearanceHistoryEvent = z.infer<typeof ClearanceHistoryEventSchema>;

export const ClearanceHistoryRecordSchema = ClearanceWithStatusSchema.extend({
  events: z.array(ClearanceHistoryEventSchema),
});
export type ClearanceHistoryRecord = z.infer<typeof ClearanceHistoryRecordSchema>;

export const ClearanceHistoryResponseSchema = z.object({
  /// Все записи работника, ВКЛЮЧАЯ аннулированные, от новых к старым.
  clearances: z.array(ClearanceHistoryRecordSchema),
  /// События, не привязанные к живой записи (например, удаление допуска
  /// прежней версией сервера). Иначе часть журнала пропала бы из ответа молча.
  orphanEvents: z.array(ClearanceHistoryEventSchema),
});
export type ClearanceHistoryResponse = z.infer<typeof ClearanceHistoryResponseSchema>;

// ─── RailSafe: Registry (сводная матрица допусков) ─────────────────────────────

export const RegistryRowSchema = z.object({
  worker: z.object({
    id: z.string().uuid(),
    employeeNumber: z.string(),
    name: z.string(),
  }),
  clearances: z.array(ClearanceWithStatusSchema),
  overallStatus: OverallStatusEnum,
  allowed: z.boolean(),
  blockedReasons: z.array(BlockedReasonSchema).optional(),
  warningReasons: z.array(WarningReasonSchema).optional(),
});
export type RegistryRow = z.infer<typeof RegistryRowSchema>;

export const RegistryResponseSchema = z.object({
  rows: z.array(RegistryRowSchema),
});
export type RegistryResponse = z.infer<typeof RegistryResponseSchema>;

// ─── Work Orders (наряд-допуск) ────────────────────────────────────────────────

export const WorkFormEnum = z.enum(['NARYAD', 'RASPORYAZHENIE', 'TEKUSCHAYA']);
export type WorkForm = z.infer<typeof WorkFormEnum>;

export const WorkOrderStatusEnum = z.enum(['ISSUED', 'ADMITTED', 'IN_PROGRESS', 'CLOSED']);
export type WorkOrderStatus = z.infer<typeof WorkOrderStatusEnum>;

export const WorkOrderMemberRoleEnum = z.enum(['MEMBER', 'OBSERVER']);
export type WorkOrderMemberRole = z.infer<typeof WorkOrderMemberRoleEnum>;

// Коды причин блокировки старта — зеркало StartReasonCode из work-orders.logic.ts.
export const StartReasonCodeEnum = z.enum([
  'PERMANENT_CLEARANCE_FAILED',
  'GROUP_BELOW_REQUIRED',
  'TARGET_BRIEFING_NOT_TODAY',
  // Инструктаж проведён, подтверждения от работника нет (№ 344, приложение 1,
  // пункт 11 — расписываются проводивший и получивший).
  'TARGET_BRIEFING_NOT_ACKNOWLEDGED',
  // Issue #531: проверка знаний после сегодняшнего инструктажа не пройдена.
  'TARGET_BRIEFING_KNOWLEDGE_FAILED',
  'WORK_ORDER_NOT_ADMITTED',
]);
export type StartReasonCode = z.infer<typeof StartReasonCodeEnum>;

export const CreateWorkOrderMemberSchema = z.object({
  workerId: z.string().uuid(),
  role: WorkOrderMemberRoleEnum.optional(), // дефолт MEMBER на сервере
});
export type CreateWorkOrderMemberDto = z.infer<typeof CreateWorkOrderMemberSchema>;

export const CreateWorkOrderSchema = z.object({
  // Номер ставит сервер при выдаче по порядку журнала организации (решение
  // владельца 25.09.2026) и присланный не читает. Поле оставлено
  // необязательным ради установленных сборок, которые его ещё присылают.
  number: z.string().min(1).optional(),
  workType: z.string().min(1),
  safetyMeasures: z.string().min(1),
  subdivisionId: z.string().uuid(),
  /** @deprecated Место работ переехало на workSiteId. Поле оставлено для обратной совместимости. */
  stationId: z.string().uuid().optional(),
  // Место проведения работ (станция или перегон) из справочника WorkSite.
  workSiteId: z.string().uuid().optional(),
  // Уточнение места в пределах места работ, напр. «км 245, пикет 7» — меняется от наряда к наряду.
  workSiteDetail: z.string().optional(),
  // требуемая группа ЭБ — enum-строка II..V (как нормативный enum из step A), null/опущено = не требуется
  requiredElectricalGroup: ElectricalGroupEnum.nullable().optional(),
  // Issue #419 (карточка B-057): напряжение электроустановки. Опущено —
  // сервер берёт умолчание строки матрицы для выбранного вида работы; ни того,
  // ни другого нет — наряд выписывается с пустым напряжением, как до задачи.
  // Поле заведено и здесь, в корневой схеме: наряд RAIL и наряд CITY
  // выписываются через неё, и без него значение молча отбрасывалось бы
  // разбором тела запроса.
  voltageClass: InstallationVoltageClassEnum.nullable().optional(),
  // Issue #146: ссылка на справочник видов работ (#144). Опционально —
  // легаси-путь с одним `workType` сохраняется. Сервер проверяет, что запись
  // принадлежит организации actor'а.
  workTypeCatalogId: z.string().uuid().nullable().optional(),
  // ADR-003 (issue #176): нормативные роли наряда разведены. `supervisorId`
  // остаётся обязательным входом для совместимости с уже выпущенными
  // клиентами и трактуется как ответственный руководитель работ.
  //   responsibleUserId — ответственный руководитель работ; если не задан,
  //     берётся supervisorId.
  //   admittingUserId — допускающий; если не задан, по умолчанию совпадает с
  //     ответственным руководителем (решение владельца от 2026-09-02).
  responsibleUserId: z.string().uuid().optional(),
  admittingUserId: z.string().uuid().optional(),
  /// Issue #326 (план Э1-4): допускающие СВЕРХ названного в admittingUserId.
  /// Пункт 9 бланка № 344 числа допускающих не называет: работы на
  /// нескольких участках допускает несколько человек, каждый на своём.
  ///
  /// НЕОБЯЗАТЕЛЬНО, и это существенно: опущено или пусто — наряд ведёт себя
  /// ровно как до задачи, включая версию 1 канона документа выдачи. Поле
  /// заведено и здесь, в корневой схеме, а не только в core: наряд RAIL и
  /// наряд CITY выписываются через неё, и без поля перечень молча отбрасывался
  /// бы разбором тела запроса.
  additionalAdmittingUserIds: z.array(z.string().uuid()).optional(),
  supervisorId: z.string().uuid(),
  producerId: z.string().uuid(),
  members: z.array(CreateWorkOrderMemberSchema).min(1),
  /// Графы 5.1–5.10 бланка. НЕОБЯЗАТЕЛЬНО: наряд прежней формы выписывается
  /// ровно как сейчас, иначе установленные сборки перестали бы выписывать
  /// наряды вовсе. Прежнее поле `safetyMeasures` остаётся обязательным — на
  /// его байтах стоят выпущенные подписи (ADR-004 §5).
  safetyMeasureItems: z.array(CreateWorkOrderSafetyMeasureSchema).max(10).optional(),
  /// Пункт 8 Правил № 344. Опущено — берётся умолчание справочника видов
  /// работ. Прислано — решает выдающий, и расхождение с умолчанием
  /// записывается вместе с тем, кто его снял.
  requiresSafetyApproval: z.boolean().optional(),
  /// Реквизиты согласования по пункту 8 (issue #468, карточка B-034): с кем
  /// согласовано, кто согласовал и когда. Поле заведено и здесь, в корневой
  /// схеме, а не только в core: наряд RAIL и наряд CITY выписываются через
  /// неё, и без него перечень молча отбрасывался бы разбором тела запроса.
  ///
  /// НЕОБЯЗАТЕЛЬНО в схеме и обязательно по существу: наряд, требующий
  /// согласования, без единой записи не выписывается — отказывает сервер, и
  /// отказывает словами. Схема этого потребовать не может: требуется ли
  /// согласование, она не знает, потому что признак берётся из справочника
  /// видов работ, когда выдающий его не трогал.
  safetyApprovals: z.array(CreateWorkOrderSafetyApprovalSchema).max(10).optional(),
  /// Земляные работы в охранной зоне подземных коммуникаций — пункт 300 Правил
  /// № 222 (issue #469, карточка B-034). Поле заведено и здесь, в корневой
  /// схеме, а не только в core: наряд RAIL и наряд CITY выписываются через неё,
  /// и без него признак молча отбрасывался бы разбором тела запроса.
  ///
  /// Опущено — берётся умолчание справочника видов работ; прислано — решает
  /// выдающий, и снятие предложенного записывается вместе с тем, кто снял.
  excavationInProtectedZone: z.boolean().optional(),
});
export type CreateWorkOrderDto = z.infer<typeof CreateWorkOrderSchema>;

// Issue #146: источник требований для ворот (см. CoreRequirementSource).
export const RequirementSourceEnum = z.enum(['MATRIX', 'LEGACY']);
export type RequirementSource = z.infer<typeof RequirementSourceEnum>;

// Результат «ворот» по одному члену бригады.
export const MemberGateResultSchema = z.object({
  workerId: z.string().uuid(),
  canStart: z.boolean(),
  reasons: z.array(StartReasonCodeEnum),
  // Issue #146: аддитивно. Человекочитаемые формулировки (в матричном режиме
  // называют вид работы и требование) и источник требований.
  messages: z.array(z.string()).optional(),
  // Те же причины разобранными: код и подстановки. Готовый текст собирается
  // на сервере по-русски и на казахском интерфейсе оставался русским.
  messageDetails: z.array(z.object({
    code: z.string(),
    params: z.record(z.string()),
  })).optional(),
  requirementSource: RequirementSourceEnum.optional(),
});
export type MemberGateResult = z.infer<typeof MemberGateResultSchema>;

// Issue #146: диагностика требований по наряду (не по члену). См.
// CoreWorkOrderReadiness в core.ts.
export const WorkOrderReadinessSchema = z.object({
  source: RequirementSourceEnum,
  workTypeCatalogId: z.string().nullable(),
  workTypeName: z.string().nullable(),
  matrixMissing: z.boolean(),
  requiredElectricalGroup: ElectricalGroupEnum.nullable(),
  requiredBriefingTypes: z.array(CoreBriefingTypeSchema),
  medicalExaminationRequired: z.boolean(),
  requiresObserver: z.boolean(),
  observerAssigned: z.boolean(),
  // Вердикт по каждому требованию — аддитивно. Установленные сборки поля не
  // читают и работают как прежде; клиент без него показывает требование
  // нейтральным кружком. Зеркало CoreRequirementVerdict из core.ts.
  requirements: z.array(z.object({
    kind: z.enum(['GROUP', 'BRIEFINGS', 'MEDICAL']),
    status: z.enum(['met', 'blocked', 'unknown']),
    failures: z.array(z.object({
      workerId: z.string(),
      reason: z.enum([
        'GROUP_BELOW_REQUIRED',
        'GROUP_MISSING',
        'CLEARANCE_MISSING',
        'CLEARANCE_EXPIRED',
        'CLEARANCE_EXPIRING',
        'CLEARANCE_NOT_CONFIGURED',
      ]),
      clearanceTypeName: z.string().nullable().optional(),
      expiresAt: z.string().nullable().optional(),
      requiredGroup: ElectricalGroupEnum.nullable().optional(),
      workerGroup: ElectricalGroupEnum.nullable().optional(),
    })),
  })).optional(),
});
export type WorkOrderReadiness = z.infer<typeof WorkOrderReadinessSchema>;
export type RequirementVerdict = WorkOrderReadiness['requirements'] extends (infer U)[] | undefined ? U : never;

export const WorkOrderSchema = z.object({
  id: z.string().uuid(),
  number: z.string(),
  workForm: WorkFormEnum,
  workType: z.string(),
  safetyMeasures: z.string(),
  requiredElectricalGroup: ElectricalGroupEnum.nullable().optional(),
  // Issue #419 (карточка B-057): напряжение электроустановки по этому наряду.
  // null — не названо; это НЕ «до 1000 В».
  voltageClass: InstallationVoltageClassEnum.nullable().optional(),
  // Issue #146: связь со справочником видов работ; null для легаси-нарядов.
  workTypeCatalogId: z.string().nullable().optional(),
  workTypeCatalogName: z.string().nullable().optional(),
  status: WorkOrderStatusEnum,
  /** @deprecated Место работ переехало на workSiteId/workSiteName. Поле оставлено для обратной совместимости. */
  stationId: z.string().nullable().optional(),
  workSiteId: z.string().nullable().optional(),
  workSiteDetail: z.string().nullable().optional(),
  subdivisionId: z.string(),
  issuerId: z.string(),
  supervisorId: z.string(),
  producerId: z.string(),
  // Читаемые имена (аддитивно; приходят из GET /work-orders и GET /work-orders/:id).
  // nullable/optional: мутационные ответы (create/admit/start/close) их не заполняют.
  /** @deprecated Место работ переехало на workSiteName. Поле оставлено для обратной совместимости. */
  stationName: z.string().nullable().optional(),
  workSiteName: z.string().nullable().optional(),
  workSiteKind: WorkSiteKindSchema.nullable().optional(),
  subdivisionName: z.string().nullable().optional(),
  issuerName: z.string().nullable().optional(),
  issuerEmployeeNumber: z.string().nullable().optional(),
  supervisorName: z.string().nullable().optional(),
  supervisorEmployeeNumber: z.string().nullable().optional(),
  producerName: z.string().nullable().optional(),
  // ADR-003: разведённые нормативные роли. nullable — исторические наряды
  // не имеют допускающего (см. миграцию 20260905120000).
  responsibleUserId: z.string().nullable().optional(),
  responsibleName: z.string().nullable().optional(),
  responsibleEmployeeNumber: z.string().nullable().optional(),
  admittingUserId: z.string().nullable().optional(),
  admittingName: z.string().nullable().optional(),
  admittingEmployeeNumber: z.string().nullable().optional(),
  producerEmployeeNumber: z.string().nullable().optional(),
  issuedAt: z.string(),
  admittedAt: z.string().nullable().optional(),
  startedAt: z.string().nullable().optional(),
  /// ADR-005 §7: первый из двух актов закрытия. Поля аддитивные и
  /// необязательные — старые сборки их игнорируют.
  ///
  /// Без них карточка не могла отличить сданное рабочее место от несданного:
  /// zod отбрасывает неизвестные ключи, поэтому `closureHandoverAt` сервера до
  /// экрана не доезжал вовсе. Кнопка сдачи оставалась после сдачи, а кнопка
  /// приёмки не появлялась никогда.
  closureHandoverAt: z.string().nullable().optional(),
  closureHandoverById: z.string().nullable().optional(),
  closureHandoverName: z.string().nullable().optional(),
  closureHandoverEmployeeNumber: z.string().nullable().optional(),
  /// Issue #327, ADR-005 §7: со-подпись ответственного руководителя работ под
  /// сдачей — по профилю организации. Тот же довод, что у полей выше: без них
  /// zod отбросил бы состояние «сдано, но ждёт со-подписи», и карточка
  /// предлагала бы приёмку, которая заведомо откажет.
  closureCoSignatureRequired: z.boolean().optional(),
  closureHandoverCoSignedAt: z.string().nullable().optional(),
  closureHandoverCoSignedById: z.string().nullable().optional(),
  closureHandoverCoSignedName: z.string().nullable().optional(),
  closureHandoverCoSignedEmployeeNumber: z.string().nullable().optional(),
  closedAt: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type WorkOrder = z.infer<typeof WorkOrderSchema>;

// Ответ на удаление ошибочного наряда (ADMIN, только ISSUED). След — в AuditEvent (WORK_ORDER_DELETED).
export const DeleteWorkOrderResponseSchema = z.object({
  id: z.string().uuid(),
  deleted: z.literal(true),
});
export type DeleteWorkOrderResponse = z.infer<typeof DeleteWorkOrderResponseSchema>;

// Issue #531: PAPER_JOURNAL — работник расписался в бумажном журнале,
// инструктирующий приложил фото страницы. PROXY_VERBAL остаётся для чтения
// прежних отметок: новых не бывает.
export const TargetBriefingAckMethodEnum = z.enum(['WORKER_DEVICE_KEY', 'PROXY_VERBAL', 'PAPER_JOURNAL']);
export type TargetBriefingAckMethod = z.infer<typeof TargetBriefingAckMethodEnum>;

export const WorkOrderMemberSchema = z.object({
  id: z.string().uuid(),
  workOrderId: z.string().uuid(),
  workerId: z.string().uuid(),
  workerName: z.string().nullable().optional(),
  workerEmployeeNumber: z.string().nullable().optional(),
  role: WorkOrderMemberRoleEnum,
  // Отметка ПРОВОДИВШЕГО: инструктаж проведён.
  targetBriefingDoneAt: z.string().nullable().optional(),
  targetBriefingById: z.string().nullable().optional(),
  // Подтверждение ПОЛУЧИВШЕГО — вторая подпись пункта 11 приложения 1 Правил
  // № 344. Способ различает собственную подпись работника и отметку за него:
  // это разные факты, и карточка обязана показывать их по-разному.
  targetBriefingAckAt: z.string().nullable().optional(),
  targetBriefingAckMethod: TargetBriefingAckMethodEnum.nullable().optional(),
  targetBriefingAckProxyById: z.string().nullable().optional(),
  targetBriefingAckProxyName: z.string().nullable().optional(),
  // Issue #531: проведение подписывает инструктирующий по каждому работнику с
  // результатом проверки знаний; «qualified» — подпись ЭЦП (иначе ключ
  // устройства, «без ЭЦП»); фото бумажного журнала, если работник расписался
  // на бумаге. Аддитивно и optional — старые ответы читаются.
  targetBriefingByName: z.string().nullable().optional(),
  targetBriefingKnowledgePassed: z.boolean().nullable().optional(),
  targetBriefingConductDocumentId: z.string().nullable().optional(),
  targetBriefingConductQualified: z.boolean().nullable().optional(),
  targetBriefingAckQualified: z.boolean().nullable().optional(),
  targetBriefingPaperMark: z
    .object({
      id: z.string(),
      fileName: z.string(),
      mimeType: z.string(),
      uploadedAt: z.string(),
      uploadedByName: z.string().nullable(),
    })
    .nullable()
    .optional(),
  // Issue #143: см. SubdivisionMemberSchema. Роль в наряде (выше) и
  // профессия по ЕТКС — разные сущности, в UI не смешиваются.
  professionName: z.string().nullable().optional(),
  qualificationGrade: z.number().int().nullable().optional(),
  professionProvisional: z.boolean().nullable().optional(),
});
export type WorkOrderMember = z.infer<typeof WorkOrderMemberSchema>;

export const WorkOrderMemberWithGateSchema = WorkOrderMemberSchema.extend({
  gate: MemberGateResultSchema,
});
export type WorkOrderMemberWithGate = z.infer<typeof WorkOrderMemberWithGateSchema>;

export const WorkOrderListItemSchema = WorkOrderSchema.extend({
  memberCount: z.number().int(),
});
export type WorkOrderListItem = z.infer<typeof WorkOrderListItemSchema>;

export const WorkOrderListResponseSchema = z.object({
  workOrders: z.array(WorkOrderListItemSchema),
});
export type WorkOrderListResponse = z.infer<typeof WorkOrderListResponseSchema>;

export const WorkOrderDetailResponseSchema = WorkOrderSchema.extend({
  members: z.array(WorkOrderMemberWithGateSchema),
  // Issue #146: аддитивно, optional — старые клиенты игнорируют.
  readiness: WorkOrderReadinessSchema.optional(),
});
export type WorkOrderDetailResponse = z.infer<typeof WorkOrderDetailResponseSchema>;

// Тело ответа 409 при блокировке старта.
export const StartBlockedResponseSchema = z.object({
  blocked: z.array(MemberGateResultSchema),
});
export type StartBlockedResponse = z.infer<typeof StartBlockedResponseSchema>;
